import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "../components/Layout";
import ShareButton from "../components/ShareButton";
import DownloadImageButton from "../components/DownloadImageButton";
import { api, TOOL_LABEL, TOOL_COLOR, TOOL_PATH } from "../lib/api";
import { ArrowRight, Eye, Ban } from "lucide-react";

const COLORS = TOOL_COLOR;
const TOOL_PATH_MAP = TOOL_PATH;

const Body = ({ tool, data }) => {
  const d = data || {};
  if (tool === "que-dibujo") {
    return (
      <>
        <h2 data-testid="shared-title" className="font-display font-black text-4xl sm:text-6xl leading-[1.05] tracking-tight mb-4">
          {d.titulo}
        </h2>
        {d.descripcion && (
          <p className="font-mono text-base sm:text-xl text-[#4a4a4a] mb-6 leading-relaxed">{d.descripcion}</p>
        )}
        {d.estilo && (
          <div className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold border-4 border-[#1a1a1a] rounded-full px-4 py-1 mb-6 bg-[#FFB000]">
            estilo: {d.estilo}
          </div>
        )}
        {Array.isArray(d.detalles) && d.detalles.length > 0 && (
          <ul className="space-y-2">
            {d.detalles.map((x, i) => (
              <li key={i} className="font-mono text-sm sm:text-base flex items-start gap-3">
                <span className="mt-1 w-3 h-3 rounded-full border-2 border-[#1a1a1a] shrink-0 bg-[#FFB000]" />
                {x}
              </li>
            ))}
          </ul>
        )}
      </>
    );
  }
  if (tool === "pen-battle") {
    return (
      <>
        <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-3 text-[#737373]">tu reto</div>
        <h2 data-testid="shared-title" className="font-display font-black text-3xl sm:text-5xl leading-tight mb-4">
          {d.reto}
        </h2>
        {d.tip && (
          <div className="bg-[#06B6D4] border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-mono text-sm font-bold tilt-1">
            💡 {d.tip}
          </div>
        )}
      </>
    );
  }
  if (tool === "emoji-battle") {
    return (
      <>
        <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-4 text-[#737373]">tu historia</div>
        <div
          data-testid="shared-title"
          className="font-display text-[18vw] sm:text-[12vw] md:text-[8rem] leading-none tracking-wider mb-6 text-center"
          style={{ fontFamily: "'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', 'Bricolage Grotesque', sans-serif" }}
        >
          {d.emojis}
        </div>
        {d.reto && (
          <div className="bg-[#EC4899] border-4 border-[#1a1a1a] rounded-2xl px-5 py-4 inline-block font-display font-black text-lg sm:text-2xl mb-4 tilt-2">
            {d.reto}
          </div>
        )}
        {d.pista && (
          <div className="bg-[#fff7e6] border-4 border-[#1a1a1a] rounded-2xl px-5 py-4 font-mono text-sm sm:text-base">
            💭 {d.pista}
          </div>
        )}
      </>
    );
  }
  if (tool === "objeto-imposible") {
    return (
      <>
        <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-3 text-[#737373]">tu objeto</div>
        <h2 data-testid="shared-title" className="font-display font-black text-4xl sm:text-6xl leading-[1.05] tracking-tight mb-4">
          {d.objeto}
        </h2>
        {d.contexto && (
          <p className="font-mono text-base sm:text-xl text-[#4a4a4a] mb-4 leading-relaxed">{d.contexto}</p>
        )}
        {d.twist && (
          <div className="bg-[#22C55E] border-4 border-[#1a1a1a] rounded-2xl px-5 py-3 inline-block font-mono text-sm sm:text-base font-bold tilt-1">
            ✦ {d.twist}
          </div>
        )}
      </>
    );
  }
  if (tool === "cliente-imposible") {
    const dif = d.dificultad || "DESAFIANTE";
    const difColor = dif === "FÁCIL" ? "#22C55E" : dif === "IMPOSIBLE" ? "#EF4444" : "#FFB000";
    return (
      <>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <span className="font-mono text-xs uppercase tracking-[0.25em] font-bold text-[#737373]">brief oficial</span>
          <span
            data-testid="dificultad-badge"
            className="inline-block font-mono text-xs uppercase tracking-[0.2em] font-black border-4 border-[#1a1a1a] rounded-full px-4 py-1 shadow-hard-sm"
            style={{ background: difColor, color: dif === "IMPOSIBLE" ? "#fff" : "#1a1a1a" }}
          >
            {dif}
          </span>
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] font-bold mb-2 text-[#737373]">El cliente dice:</div>
        <blockquote data-testid="shared-title" className="font-display italic text-xl sm:text-2xl text-[#1a1a1a] leading-snug border-l-4 border-[#1a1a1a] pl-4 mb-6">
          &ldquo;{d.brief}&rdquo;
        </blockquote>
        <div className="font-mono text-xs uppercase tracking-[0.2em] font-bold mb-2 text-[#737373]">Lo que realmente quiere:</div>
        <p className="font-mono text-sm sm:text-base text-[#1a1a1a] leading-relaxed">{d.interpretacion}</p>
      </>
    );
  }
  if (tool === "paleta-maldita") {
    const colores = d.colores || [];
    const nombres = d.nombres || [];
    return (
      <>
        <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-5 text-[#737373]">tu paleta</div>
        <div data-testid="swatches" className="flex flex-wrap gap-5 sm:gap-7 mb-8">
          {colores.map((hex, i) => (
            <div key={i} className="flex flex-col items-start">
              <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-2xl border-4 border-[#1a1a1a] shadow-hard-sm" style={{ background: hex }} />
              <div className="mt-3 font-mono text-xs sm:text-sm font-bold text-[#1a1a1a]">{nombres[i]}</div>
              <div className="font-mono text-xs text-[#737373] tabular-nums">{hex}</div>
            </div>
          ))}
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-2 text-[#737373]">el reto</div>
        <h2 data-testid="shared-title" className="font-display font-black text-2xl sm:text-4xl leading-tight mb-3">{d.reto}</h2>
        <p className="font-mono text-xs text-[#737373]">Regla: solo blanco y negro para texto.</p>
      </>
    );
  }
  if (tool === "tipografia-torturada") {
    return (
      <div className="text-center">
        <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-5 text-[#737373]">la palabra</div>
        <div
          data-testid="palabra"
          className="font-display font-black text-[#1a1a1a] tracking-tight leading-none mb-10 break-words"
          style={{ fontSize: "clamp(3rem, 11vw, 7rem)" }}
        >
          {d.palabra}
        </div>
        <div className="border-t-4 border-[#1a1a1a] pt-6 max-w-lg mx-auto">
          <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-3 text-[#737373]">debes escribirla en:</div>
          <div data-testid="shared-title" className="font-display font-bold text-xl sm:text-2xl text-[#6366F1] mb-3">{d.estilo}</div>
          {d.contradiccion && (
            <p className="font-mono italic text-xs sm:text-sm text-[#737373]">{d.contradiccion}</p>
          )}
        </div>
      </div>
    );
  }
  if (tool === "logo-en-60") {
    return (
      <>
        <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-2 text-[#737373]">la marca</div>
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <h2 data-testid="logo-marca" className="font-display font-black text-3xl sm:text-5xl tracking-tight">{d.marca}</h2>
          <span className="font-mono text-xs uppercase tracking-wider font-bold border-2 border-[#1a1a1a] rounded-full px-3 py-1 bg-[#dcfce7]">
            {d.sector}
          </span>
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.25em] font-bold mb-2 text-[#737373]">el reto</div>
        <p data-testid="shared-title" className="font-mono text-base sm:text-lg text-[#1a1a1a] mb-6 leading-relaxed">{d.sentimiento}</p>
        <div className="bg-[#FEF2F2] border-4 border-[#1a1a1a] rounded-2xl p-4 sm:p-5 flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg border-2 border-[#7f1d1d] bg-white flex items-center justify-center shrink-0">
            <Ban className="w-4 h-4 text-[#7f1d1d]" strokeWidth={3} />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] font-bold text-[#7f1d1d] mb-1">restricción</div>
            <div className="font-mono text-sm sm:text-base font-bold text-[#7f1d1d]">{d.restriccion}</div>
          </div>
        </div>
      </>
    );
  }
  return <p className="font-mono">{d.raw || ""}</p>;
};

const SharedPrompt = () => {
  const { id } = useParams();
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await api.get(`/prompts/${id}`);
        if (active) setDoc(res.data);
      } catch (e) {
        if (active) setError(e?.response?.status === 404 ? "Este prompt no existe (o ya se voló)." : "No se pudo cargar.");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [id]);

  const color = doc ? COLORS[doc.tool] : "#22C55E";
  const toolLabel = doc ? TOOL_LABEL[doc.tool] || doc.tool : "";
  const toolPath = doc ? TOOL_PATH_MAP[doc.tool] || "/" : "/";

  return (
    <Layout accent={color}>
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 sm:pt-16 pb-24">
        {loading && (
          <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-10 text-center font-mono text-sm uppercase tracking-[0.25em]">
            Cargando...
          </div>
        )}

        {!loading && error && (
          <div className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-md p-10 text-center pop-in" data-testid="shared-error">
            <div className="font-display font-black text-4xl mb-3">404</div>
            <p className="font-mono text-sm text-[#4a4a4a] mb-6">{error}</p>
            <Link
              to="/"
              className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-mono text-xs uppercase tracking-wider font-bold shadow-hard-sm"
            >
              ir al hub <ArrowRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        )}

        {!loading && doc && (
          <>
            <div className="mb-6 flex items-center gap-3 flex-wrap">
              <span
                className="inline-block font-mono text-xs uppercase tracking-[0.25em] font-bold border-4 border-[#1a1a1a] rounded-full px-4 py-1"
                style={{ background: color }}
              >
                {toolLabel}
              </span>
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#737373]">
                {new Date(doc.created_at).toLocaleString("es")}
              </span>
              <span
                data-testid="view-counter"
                className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.2em] font-bold border-2 border-[#1a1a1a] rounded-full px-3 py-1 bg-white"
                title="vistas"
              >
                <Eye className="w-3.5 h-3.5" strokeWidth={3} />
                {doc.views || 1} {(doc.views || 1) === 1 ? "vista" : "vistas"}
              </span>
            </div>

            <div
              ref={cardRef}
              data-testid="shared-prompt-card"
              className="bg-white border-4 border-[#1a1a1a] rounded-3xl shadow-hard-lg p-6 sm:p-10 pop-in mb-6 relative"
            >
              <div className="absolute top-3 right-4 font-mono text-[10px] uppercase tracking-[0.25em] text-[#737373] font-bold">
                guango · {toolLabel.toLowerCase()}
              </div>
              <Body tool={doc.tool} data={doc.extra || {}} />
            </div>

            <div className="flex flex-wrap gap-3" data-exclude-from-image="true">
              <Link
                to={toolPath}
                data-testid="shared-try-tool"
                className="press inline-flex items-center gap-2 bg-[#1a1a1a] text-white border-4 border-[#1a1a1a] rounded-xl px-5 py-3 font-display font-black text-lg shadow-hard-md"
              >
                probar la herramienta <ArrowRight className="w-5 h-5" strokeWidth={3} />
              </Link>
              <ShareButton promptId={doc.id} color={color} testId="shared-page-share" />
              <DownloadImageButton
                targetRef={cardRef}
                fileName={`guango-${doc.tool}-${doc.id.slice(0, 8)}.png`}
                color={color}
                testId="shared-page-download"
              />
            </div>
          </>
        )}
      </section>
    </Layout>
  );
};

export default SharedPrompt;
